import React from "react";
import Cabinet from "../models/Cabinet";
import Page from "../models/Page";
import ActorView from "./ActorView";
import { PagePrintInfo } from "../models/PagePrintInfo";
import Actor from "../models/Actor";
import CabinetTreeViewModel from "../modelTreeViews/CabinetTreeviewModel";
import { useOnChangeWatcher } from "../models/useOnChangeWatcher";
import { useRedraw } from "../models/useRerender";
import { isUiModeExpert, isUiModeUser } from "../uiMode";
import type Consumer from "../models/Consumer";

export default function ActorsView(props: { page: Page; fixed: boolean; cabinet: Cabinet; print?: PagePrintInfo }) {
  const { page, fixed, cabinet, print } = props;

  const redraw = useRedraw();

  useOnChangeWatcher(state.selectionManager);

  useOnChangeWatcher(state.uiMode);

  useOnChangeWatcher(cabinet.actors, (changedProperty, changedObject) => {
    //
    // delay, to make sure consumer is in dom-model,
    // to allow connection endpoint to be calculated
    //
    redraw();
    setTimeout(() => {
      page.updateConnections();
    }, 200);
    return true;
  });

  if (cabinet) {
    const children: any = [];

    let sortedActors = new Array<Actor>();
    if (isUiModeUser()) {
      const consumer = state.selectionManager.selectedObject as Consumer;
      if (consumer?.isConsumer) {
        // Show only the selected consumer's cabinet
        const actor = consumer.getConnectedChannel()?.parent as Actor;

        if (actor) {
          sortedActors = [actor];
        } else {
          return null;
        }
      } else {
        // Selection changed to something else (e.g. no longer a consumer) between renders.
        return null;
      }
    } else {
      sortedActors = cabinet.actors.sortBy("tagOrName");
    }

    let showUsedOnly = cabinet.showUsedActorsOnly;
    let showUsedChannels = true;
    if (print) {
      showUsedOnly = true;
      showUsedChannels = true;
    }

    let actorsToShow: Array<Actor>;
    if (showUsedOnly) {
      const selectedOnly = !isUiModeExpert();
      actorsToShow = sortedActors.filter((actor) => actor.hasConnectionOnPage(page, selectedOnly));
    } else {
      actorsToShow = sortedActors;
    }

    actorsToShow.forEach((actor) => {
      if (actor.className == "Actor") {
        children.push(
          <ActorView
            key={actor.id}
            actor={actor}
            fixed={fixed}
            page={page}
            print={print}
            showUsedOnly={showUsedOnly}
            showUsedChannels={showUsedChannels}
          />,
        );
      }
    });

    if (children.length == 0) {
      if (cabinet.actors?.size > 0) {
        children.push(
          <div
            key="noConnectionToCabinetOnPage"
            style={{
              width: "90px",
              fontSize: "10px",
              textAlign: "center",
              marginLeft: "-10px",
            }}
          >
            {state.translate(
              "ActorView.noConnectionsToActorOnThisPage",
              "es sind keine Verbindungen zu einem Aktor auf dieser Seite bekannt",
            )}
          </div>,
        );
      } else {
        children.push(
          <div
            key="noActoInCabinet"
            style={{
              width: "90px",
              fontSize: "10px",
              textAlign: "center",
              marginLeft: "-10px",
            }}
          >
            {state.translate("ActorView.noActorsInCabinet", " in diesem Schaltschrank sind keine Aktoren vorhanden")}
          </div>,
        );
        if (!print) {
          children.push(
            <button
              id={"button_newActor"} // no itx
              className="button"
              key={"button_newActor"} // no itx
              style={{
                width: "90px",
                fontSize: "9px",
                textAlign: "center",
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                pointerEvents: "auto",
                marginLeft: "-10px",
              }}
              onClick={(ev) => {
                ev.stopPropagation();
                (cabinet.treeViewModel as CabinetTreeViewModel).handleAddActor("Actor"); // no itx
                cabinet.raiseOnChange("actors");
              }}
            >
              <div>{state.translate("ActorView.newActor", "neuer Aktor")}</div>
              <i
                className="las la-plus-circle"
                style={{
                  fontSize: "15px",
                  color: "var(--textColorDarkGrey)",
                  marginLeft: "auto",
                }}
              ></i>
            </button>,
          );
        }
      }
    }

    return (
      <div
        id={"allActorsOfCabinet" + cabinet.id}
        style={{
          position: "relative",
          width: "max-content",
          pointerEvents: "none",
          paddingBottom: "20px",
        }}
      >
        {children}
      </div>
    );
  } else {
    return null;
  }
}

